import { hasValue } from '../components/PreviewCards/utils';

import { toBoolean } from './utils';

import type {
  PropertyDeed,
  PropertyDetails,
  PropertyEquity,
  PropertyLoan,
  PropertyMarketHistoryEntry,
  PropertyOwner,
  PropertyPermit,
  PropertyReportData,
  PropertyResident,
  PropertyTaxAssessment,
  PropertyTaxBill,
  PropertyValuationEstimate,
} from '../types';

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toString(value: unknown): string {
  return value == null ? '' : String(value);
}

function toCount(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
}

function parseNumber(value: unknown): number | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const n = Number(String(value).replace(/[$,]/g, ''));
  return Number.isFinite(n) ? n : undefined;
}

function splitBuyersSellers(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((entry) =>
    String(entry)
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean)
  );
}

function normalizeOwners(raw: unknown): PropertyOwner[] {
  const ownersRoot = asRecord(raw);
  const list = asArray(ownersRoot?.owners);

  return list
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      name: toString(item.name),
      first: toString(item.first),
      last: toString(item.last),
    }))
    .filter((owner) => owner.name.length > 0);
}

function normalizeResidents(raw: unknown): PropertyResident[] {
  const residentsRoot = asRecord(raw);
  const list = asArray(residentsRoot?.residents);

  return list
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      name: toString(item.name),
      aliases: asArray(item.aliases).map((alias) => String(alias)),
      phones: asArray(item.phones)
        .map((phone) => asRecord(phone))
        .filter((phone): phone is Record<string, unknown> => phone !== null)
        .map((phone) => ({
          number: toString(phone.number),
          type: toString(phone.type),
        })),
      currentAddress: toString(item.current_address ?? item.currentAddress),
      firstSeenDate: toString(item.first_seen_date ?? item.firstSeenDate),
      lastSeenDate: toString(item.last_seen_date ?? item.lastSeenDate),
    }))
    .filter((resident) => resident.name.length > 0);
}

function normalizeDeeds(raw: unknown): PropertyDeed[] {
  const deedsRoot = asRecord(raw);
  const list = asArray(deedsRoot?.deeds);

  return list
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      recordingDate: toString(item.recording_date ?? item.recordingDate),
      filingDate: toString(item.filing_date ?? item.filingDate) || undefined,
      buyers: splitBuyersSellers(item.buyers),
      sellers: splitBuyersSellers(item.sellers),
      deedType: toString(item.deed_type ?? item.deedType),
      transferType: toString(item.transfer_type ?? item.transferType),
      documentNumber: toString(item.document_number ?? item.documentNumber),
      transferValue:
        parseNumber(item.transfer_value ?? item.transferValue) ?? null,
      quitclaim: toBoolean(item.quitclaim),
      loans: asArray(item.loans)
        .map((loan) => asRecord(loan))
        .filter((loan): loan is Record<string, unknown> => loan !== null)
        .map((loan) => ({
          lender: toString(loan.lender),
          loanAmount: parseNumber(loan.loan_amount ?? loan.loanAmount),
          loanType: toString(loan.loan_type ?? loan.loanType),
          lenderType: toString(loan.lender_type ?? loan.lenderType),
        })),
    }))
    .filter(
      (deed) => deed.documentNumber.length > 0 || deed.recordingDate.length > 0
    );
}

function normalizePermits(raw: unknown): PropertyPermit[] {
  const permitsRoot = asRecord(raw);
  const list = asArray(permitsRoot?.permits);

  return list
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      permitNumber: toString(item.permit_number ?? item.permitNumber),
      subType: toString(item.sub_type ?? item.subType ?? item.type),
      description: toString(item.description ?? item.desc),
      date: toString(item.date),
      requestor: toString(item.requestor),
      jobValue: parseNumber(item.job_value ?? item.jobValue),
    }))
    .filter(
      (permit) => permit.permitNumber.length > 0 || permit.subType.length > 0
    );
}

function normalizeTaxAssessments(raw: unknown): PropertyTaxAssessment[] {
  const taxesRoot = asRecord(raw);
  const list = asArray(taxesRoot?.assessments);

  return list
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      year: toCount(item.year),
      total: parseNumber(item.total) ?? null,
      land: parseNumber(item.land) ?? null,
      improvements: parseNumber(item.improvements) ?? null,
    }))
    .filter((assessment) => assessment.year > 0);
}

function normalizeTaxBills(raw: unknown): PropertyTaxBill[] {
  const taxesRoot = asRecord(raw);
  const list = asArray(taxesRoot?.bills);

  return list
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      year: toCount(item.year),
      amount: parseNumber(item.amount) ?? null,
    }))
    .filter((bill) => bill.year > 0);
}

function buildDetails(
  value: Record<string, unknown>
): PropertyDetails | undefined {
  const ownersRoot = asRecord(value.owners);
  const county = toString(value.county);
  const apn = toString(value.apn);
  const zoning = toString(value.zoning);
  const propertyType = toString(value.propertyType);
  const lotSqFt = parseNumber(value.lotSqFt);
  const buildingSqFt = parseNumber(value.buildingSqFt);
  const stories = parseNumber(value.stories);
  const units = parseNumber(value.units);
  const ownerVesting = toString(
    ownersRoot?.owner_vesting ?? ownersRoot?.ownerVesting
  );

  if (
    !county &&
    !apn &&
    !zoning &&
    !propertyType &&
    lotSqFt == null &&
    buildingSqFt == null &&
    stories == null &&
    units == null &&
    !ownerVesting
  ) {
    return undefined;
  }

  return {
    county,
    apn,
    zoning,
    landUse: propertyType,
    block: '',
    section: '',
    lotSizeSqFt: lotSqFt ?? 0,
    frontageFt: 0,
    depthFt: 0,
    buildingAreaSqFt: buildingSqFt ?? 0,
    stories: stories ?? 0,
    units: units ?? 0,
    ownerVesting,
    pool: '',
  };
}

function buildRentalValuation(value: Record<string, unknown>) {
  const valueAmount = parseNumber(value.rentalValue);
  const minValue = parseNumber(value.rentalMinValue);
  const maxValue = parseNumber(value.rentalMaxValue);

  if (valueAmount == null && minValue == null && maxValue == null) {
    return undefined;
  }

  return {
    value: valueAmount ?? minValue ?? maxValue ?? 0,
    minValue: minValue ?? valueAmount ?? 0,
    maxValue: maxValue ?? valueAmount ?? 0,
    confidenceScore: 0,
    date: '',
  };
}

function normalizeMarketHistory(raw: unknown): PropertyMarketHistoryEntry[] {
  return asArray(raw)
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      date: toString(item.date),
      total: parseNumber(item.total) ?? 0,
    }))
    .filter((entry) => entry.date.length > 0);
}

function normalizeValuationEstimates(
  raw: unknown
): PropertyValuationEstimate[] {
  return asArray(raw)
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      source: toString(item.source),
      date: toString(item.date),
      total: parseNumber(item.total ?? item.value) ?? 0,
      min: parseNumber(item.min ?? item.low) ?? 0,
      max: parseNumber(item.max ?? item.high) ?? 0,
      confidenceScore:
        parseNumber(item.confidence_score ?? item.confidenceScore) ?? 0,
    }))
    .filter((est) => est.total > 0 || est.source.length > 0);
}

function normalizeEquities(raw: unknown): PropertyEquity[] {
  return asArray(raw)
    .map((item) => asRecord(item))
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      altv: parseNumber(item.altv) ?? 0,
      estimatedAvailableEquity:
        parseNumber(
          item.estimated_available_equity ?? item.estimatedAvailableEquity
        ) ?? 0,
      estimatedLendableEquity:
        parseNumber(
          item.estimated_lendable_equity ?? item.estimatedLendableEquity
        ) ?? 0,
      publicationDate: toString(item.publication_date ?? item.publicationDate),
      loans: asArray(item.loans)
        .map((loan) => asRecord(loan))
        .filter((loan): loan is Record<string, unknown> => loan !== null)
        .map(
          (loan): PropertyLoan => ({
            id: loan.id ? toString(loan.id) : undefined,
            value: parseNumber(loan.value),
            date: loan.date ? toString(loan.date) : undefined,
            lender: loan.lender ? toString(loan.lender) : undefined,
            loanAmount: parseNumber(loan.loan_amount ?? loan.loanAmount),
            loanType:
              loan.loan_type ?? loan.loanType
                ? toString(loan.loan_type ?? loan.loanType)
                : undefined,
            lenderType:
              loan.lender_type ?? loan.lenderType
                ? toString(loan.lender_type ?? loan.lenderType)
                : undefined,
          })
        ),
    }));
}

/**
 * A property report is "empty" when every box is blank: no address/location, no
 * characteristics or values, all counts zero, and no structured records. Default
 * values (e.g. `ownerOccupied: "Unknown"`) do not count as information. Used to
 * suppress empty deepsearch previews.
 */
export function isPropertyReportEmpty(data: PropertyReportData): boolean {
  const hasText =
    hasValue(data.address) ||
    hasValue(data.city) ||
    hasValue(data.state) ||
    hasValue(data.zip) ||
    hasValue(data.propertyType) ||
    hasValue(data.beds) ||
    hasValue(data.baths) ||
    hasValue(data.yearBuilt) ||
    hasValue(data.lotSqFt) ||
    hasValue(data.estimatedValue) ||
    hasValue(data.assessedValue) ||
    hasValue(data.annualTax) ||
    hasValue(data.annualTaxYear) ||
    hasValue(data.currentOwner) ||
    hasValue(data.ownerOccupied);

  const hasCounts =
    data.ownersCount > 0 ||
    data.taxesCount > 0 ||
    data.buildingPermitsCount > 0 ||
    data.residentsCount > 0 ||
    data.deedsCount > 0;

  const hasRecords = Boolean(
    data.details ||
      data.rentalValuation ||
      (data.lastSoldPrice ?? 0) > 0 ||
      hasValue(data.lastSoldDate ?? '') ||
      (data.owners?.length ?? 0) > 0 ||
      (data.residents?.length ?? 0) > 0 ||
      (data.deeds?.length ?? 0) > 0 ||
      (data.permits?.length ?? 0) > 0 ||
      (data.marketHistory?.length ?? 0) > 0 ||
      (data.valuationEstimates?.length ?? 0) > 0 ||
      (data.equities?.length ?? 0) > 0 ||
      (data.taxBills?.length ?? 0) > 0 ||
      (data.taxAssessments?.length ?? 0) > 0
  );

  return !hasText && !hasCounts && !hasRecords;
}

export function mapPropertyReportPayload(
  value: Record<string, unknown>
): PropertyReportData {
  const owners = normalizeOwners(value.owners);
  const residents = normalizeResidents(value.residents);
  const deeds = normalizeDeeds(value.deeds);
  const permits = normalizePermits(
    value.buildingPermits ?? value.building_permits
  );
  const taxAssessments = normalizeTaxAssessments(value.taxes);
  const taxBills = normalizeTaxBills(value.taxes);
  const details = buildDetails(value);
  const rentalValuation = buildRentalValuation(value);
  const lastSoldPrice = parseNumber(value.lastSoldPrice);
  const marketHistory = normalizeMarketHistory(
    value.market_history ?? value.marketHistory
  );
  const valuationEstimates = normalizeValuationEstimates(
    value.current_estimates ?? value.currentEstimates
  );
  const equities = normalizeEquities(value.equities ?? value.equity);

  return {
    address: toString(value.address),
    city: toString(value.city),
    state: toString(value.state),
    zip: toString(value.zip),
    propertyType: toString(value.propertyType),
    beds: toString(value.beds),
    baths: toString(value.baths),
    yearBuilt: toString(value.yearBuilt),
    lotSqFt: toString(value.lotSqFt),
    estimatedValue: toString(value.estimatedValue),
    currentOwner: toString(value.currentOwner),
    ownerOccupied: toString(value.ownerOccupied),
    annualTax: toString(value.annualTax),
    annualTaxYear: toString(value.annualTaxYear),
    assessedValue: toString(value.assessedValue),
    ownersCount: toCount(value.ownersCount) || owners.length,
    taxesCount: toCount(value.taxesCount) || taxBills.length,
    buildingPermitsCount: toCount(value.buildingPermitsCount) || permits.length,
    residentsCount: toCount(value.residentsCount) || residents.length,
    deedsCount: toCount(value.deedsCount) || deeds.length,
    details,
    taxBills,
    taxAssessments,
    rentalValuation,
    lastSoldPrice,
    lastSoldDate: toString(value.lastSoldDate) || undefined,
    owners: owners.length > 0 ? owners : undefined,
    residents: residents.length > 0 ? residents : undefined,
    deeds: deeds.length > 0 ? deeds : undefined,
    permits: permits.length > 0 ? permits : undefined,
    marketHistory: marketHistory.length > 0 ? marketHistory : undefined,
    valuationEstimates:
      valuationEstimates.length > 0 ? valuationEstimates : undefined,
    equities: equities.length > 0 ? equities : undefined,
  };
}
